import { useEffect, useRef, useState } from 'react'

const STROKE_WIDTH = 2
// indigo-900 — canvas strokes can't read Tailwind tokens
const STROKE_COLOR = '#0f0a66'
const SIGNATURE_MIME_TYPE = 'image/png'

interface UseSignaturePadProps {
  onChange?: (dataUrl: string | undefined) => void
}

export function useSignaturePad({ onChange }: UseSignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const [hasSignature, setHasSignature] = useState(false)
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    function resizeCanvas(shouldNotify: boolean) {
      if (!canvas) return
      const ratio = window.devicePixelRatio || 1
      const { width, height } = canvas.getBoundingClientRect()
      canvas.width = width * ratio
      canvas.height = height * ratio
      const context = canvas.getContext('2d')
      if (!context) return
      context.scale(ratio, ratio)
      context.lineWidth = STROKE_WIDTH
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.strokeStyle = STROKE_COLOR
      // Resizing wipes the canvas, so the stored signature is no longer valid
      setHasSignature(false)
      if (shouldNotify) onChangeRef.current?.(undefined)
    }

    const handleResize = () => resizeCanvas(true)
    resizeCanvas(false)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  function getPoint(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const context = canvasRef.current?.getContext('2d')
    if (!context) return
    e.currentTarget.setPointerCapture(e.pointerId)
    isDrawingRef.current = true
    const { x, y } = getPoint(e)
    context.beginPath()
    context.moveTo(x, y)
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) return
    const context = canvasRef.current?.getContext('2d')
    if (!context) return
    const { x, y } = getPoint(e)
    context.lineTo(x, y)
    context.stroke()
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
    setHasSignature(true)
    onChange?.(canvasRef.current?.toDataURL(SIGNATURE_MIME_TYPE))
  }

  function handleClear() {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return
    context.clearRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
    onChange?.(undefined)
  }

  return {
    canvasRef,
    hasSignature,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleClear,
  }
}
